(() => {
    const MAX_QUANTITY = 20;
    const MAX_MESSAGE = 'You can add a maximum of 20 units of each product.';
    const syncedSelectors = new WeakSet();
    const pendingSelectors = new WeakSet();
    const observedUpsellRoots = new WeakSet();
    const inputTimers = new WeakMap();
    let cartCache = null;
    let cartCacheTime = 0;
    let hideTimer;

    const getInventoryMessage = (limit, result = {}) => {
        const numericLimit = Number(limit);
        if (numericLimit >= MAX_QUANTITY) return MAX_MESSAGE;
        if (numericLimit <= 0) return 'This product is currently unavailable for delivery to your selected location.';

        const store = result.fulfillment_source === 'default_store' && result.default_store
            ? result.default_store.name
            : result.nearest_store?.name || result.nearest_store_stock?.name;
        const storeText = store ? ` at ${store}` : '';
        const unitText = numericLimit === 1 ? 'unit is' : 'units are';
        return `Only ${numericLimit} ${unitText} currently available${storeText} for delivery to your location.`;
    };

    const showError = (limit = MAX_QUANTITY, message = '') => {
        let toast = document.querySelector('[data-cart-quantity-limit-toast]');
        if (!toast) {
            toast = document.createElement('div');
            toast.className = 'cart-quantity-limit-toast';
            toast.dataset.cartQuantityLimitToast = '';
            toast.setAttribute('role', 'alert');
            toast.setAttribute('aria-live', 'assertive');
            document.body.appendChild(toast);
        }

        toast.textContent = message || getInventoryMessage(limit);
        toast.classList.remove('is-visible');
        window.requestAnimationFrame(() => toast.classList.add('is-visible'));
        window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 4000);
    };

    const readLocation = () => {
        try {
            const savedLocation = window.sessionStorage.getItem('cw_location');
            return savedLocation ? JSON.parse(savedLocation) : null;
        } catch (error) {
            return null;
        }
    };

    const getCart = async (force = false) => {
        if (!force && cartCache && Date.now() - cartCacheTime < 750) return cartCache;
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Could not load the cart.');
        cartCache = await response.json();
        cartCacheTime = Date.now();
        return cartCache;
    };

    const checkInventory = async (variantId, cartQuantity) => {
        const cart = await getCart(true);
        const location = readLocation();
        const payload = {
            shop: window.Shopify?.shop || document.querySelector('meta[name="shopify-shop"]')?.content || '',
            variant_id: String(variantId),
            cart_items: cart.items,
            cart_quantity: String(cartQuantity)
        };

        if (location?.lat != null && location?.lng != null) {
            payload.lat = location.lat;
            payload.lng = location.lng;
        }

        const response = await fetch('/apps/cw/product-inventory-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Inventory check failed.');
        return response.json();
    };

    const getResultLimit = (result) => {
        if (result.purchasable) return MAX_QUANTITY;
        const available = Number(result.max_available_quantity);
        return Number.isFinite(available)
            ? Math.max(0, Math.min(available, MAX_QUANTITY))
            : 0;
    };

    const getCartItemData = (selector) => {
        try {
            return JSON.parse(selector.closest('cart-item')?.getAttribute('item') || 'null');
        } catch (error) {
            return null;
        }
    };

    const setSelectorLimit = (selector, limit) => {
        const safeLimit = Math.max(1, Math.min(Number(limit) || 1, MAX_QUANTITY));
        const input = selector.querySelector('input[type="number"]');
        selector.setAttribute('max', String(safeLimit));
        if ('max' in selector) selector.max = safeLimit;
        if (input) input.setAttribute('max', String(safeLimit));
        return safeLimit;
    };

    const UPSELL_SCOPE = 'lb-upsell-widget, lb-upsell-flat-card, .lb-widget, .lb-addon-cont, [class*="lb-upsell"]';
    const UPSELL_INPUT = 'input[type="number"], input[name="quantity"], input[class*="quantity"]';

    const belongsToUpsell = (node) => {
        let current = node instanceof Element ? node : null;

        while (current) {
            if (current.matches?.(UPSELL_SCOPE) || current.tagName?.startsWith('LB-')) return true;

            const root = current.getRootNode?.();
            current = root instanceof ShadowRoot ? root.host : current.parentElement;
        }

        return false;
    };

    const clampUpsellInput = (input, notify = false) => {
        if (!(input instanceof HTMLInputElement) || !belongsToUpsell(input)) return false;

        input.setAttribute('max', String(MAX_QUANTITY));
        const value = Number(input.value);
        if (!Number.isFinite(value) || value <= MAX_QUANTITY) return false;

        input.value = String(MAX_QUANTITY);
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        if (notify) showError(MAX_QUANTITY);
        return true;
    };

    const enforceUpsellLimits = (root) => {
        if (!root?.querySelectorAll) return;

        root.querySelectorAll(UPSELL_INPUT).forEach((input) => clampUpsellInput(input));
        root.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) observeUpsellRoot(element.shadowRoot);
        });
    };

    const findUpsellQuantityInput = (trigger) => {
        let current = trigger instanceof Element ? trigger : null;

        while (current) {
            const input = current.querySelector?.(UPSELL_INPUT);
            if (input && belongsToUpsell(input)) return input;

            const root = current.getRootNode?.();
            current = current.parentElement || (root instanceof ShadowRoot ? root.host : null);
            if (current?.matches?.(UPSELL_SCOPE)) {
                const scopedInput = current.shadowRoot?.querySelector(UPSELL_INPUT) || current.querySelector?.(UPSELL_INPUT);
                if (scopedInput) return scopedInput;
            }
        }

        return null;
    };

    const handleUpsellClick = (event) => {
        const eventRoot = event.currentTarget;
        const path = event.composedPath?.() || [];
        const trigger = path.find((node) => node instanceof Element && node.matches('button, [role="button"]'));
        if (!trigger || !belongsToUpsell(trigger)) return;

        const label = `${trigger.getAttribute('aria-label') || ''} ${trigger.getAttribute('title') || ''} ${trigger.textContent || ''}`.trim();
        const isIncrease = /increase|increment|plus/i.test(label) || trigger.textContent?.trim() === '+';
        if (!isIncrease) {
            window.setTimeout(() => enforceUpsellLimits(eventRoot), 0);
            return;
        }

        const input = findUpsellQuantityInput(trigger);
        if (input && Number(input.value) >= MAX_QUANTITY) {
            event.preventDefault();
            event.stopImmediatePropagation();
            clampUpsellInput(input);
            showError(MAX_QUANTITY);
            return;
        }

        window.setTimeout(() => {
            if (input) clampUpsellInput(input, true);
            enforceUpsellLimits(eventRoot);
        }, 0);
    };

    function observeUpsellRoot(root) {
        if (!root || observedUpsellRoots.has(root)) return;
        observedUpsellRoots.add(root);

        root.addEventListener('click', handleUpsellClick, true);
        root.addEventListener('input', (event) => {
            if (event.target instanceof HTMLInputElement) clampUpsellInput(event.target, true);
        }, true);

        if (root !== document) {
            const rootObserver = new MutationObserver(() => enforceUpsellLimits(root));
            rootObserver.observe(root, { childList: true, subtree: true });
        }
        enforceUpsellLimits(root);
    }

    const syncCartSelector = async (selector) => {
        if (syncedSelectors.has(selector)) return;
        syncedSelectors.add(selector);

        const item = getCartItemData(selector);
        if (!item) return;

        try {
            const result = await checkInventory(item.variant_id || item.id, MAX_QUANTITY);
            const limit = getResultLimit(result);
            const safeLimit = setSelectorLimit(selector, limit || 1);
            const currentQuantity = Number(selector.value || selector.querySelector('input')?.value || item.quantity);

            if (limit <= 0) {
                showError(0, getInventoryMessage(0, result));
            } else if (currentQuantity > safeLimit) {
                selector.value = safeLimit;
                showError(safeLimit, getInventoryMessage(safeLimit, result));
            }
        } catch (error) {
            setSelectorLimit(selector, Math.min(Number(selector.getAttribute('max')) || MAX_QUANTITY, MAX_QUANTITY));
        }
    };

    const validateAndSetCartQuantity = async (selector, requestedQuantity) => {
        const item = getCartItemData(selector);
        if (!item || pendingSelectors.has(selector)) return;

        if (requestedQuantity > MAX_QUANTITY) {
            setSelectorLimit(selector, MAX_QUANTITY);
            showError(MAX_QUANTITY);
            return;
        }

        pendingSelectors.add(selector);
        const input = selector.querySelector('input[type="number"]');
        const previousQuantity = Number(selector.value || item.quantity || 1);

        try {
            const result = await checkInventory(item.variant_id || item.id, requestedQuantity);
            if (result.purchasable) {
                selector.value = requestedQuantity;
                cartCache = null;
                return;
            }

            const limit = getResultLimit(result);
            if (limit > 0) setSelectorLimit(selector, limit);
            if (input) input.value = String(previousQuantity);
            showError(limit, getInventoryMessage(limit, result));
        } catch (error) {
            if (input) input.value = String(previousQuantity);
            showError(null, 'Current inventory could not be verified. Please try again.');
        } finally {
            pendingSelectors.delete(selector);
        }
    };

    const applyLimit = (root = document) => {
        const selectors = [];
        if (root.matches?.('quantity-selector')) selectors.push(root);
        root.querySelectorAll?.('quantity-selector').forEach((selector) => selectors.push(selector));

        selectors.forEach((selector) => {
            const input = selector.querySelector('input[type="number"]');
            const configuredMax = Number(input?.getAttribute('max') || selector.getAttribute('max'));
            setSelectorLimit(selector, configuredMax > 0 ? configuredMax : MAX_QUANTITY);
            if (selector.closest('cart-item')) syncCartSelector(selector);
        });

        root.querySelectorAll?.('form[action*="/cart/add"] input[name="quantity"]').forEach((input) => {
            input.setAttribute('max', String(MAX_QUANTITY));
        });
    };

    document.addEventListener('click', async (event) => {
        const increaseButton = event.target.closest('quantity-selector [data-ref="increaseButton"]');
        if (!increaseButton) return;

        const selector = increaseButton.closest('quantity-selector');
        const input = selector?.querySelector('input[type="number"]');
        const effectiveMax = Math.min(Number(input?.getAttribute('max')) || MAX_QUANTITY, MAX_QUANTITY);
        const currentQuantity = Number(input?.value || 0);

        if (currentQuantity >= effectiveMax) {
            event.preventDefault();
            event.stopImmediatePropagation();
            showError(effectiveMax);
            return;
        }

        if (selector.closest('cart-item')) {
            event.preventDefault();
            event.stopImmediatePropagation();
            await validateAndSetCartQuantity(selector, currentQuantity + 1);
        }
    }, true);

    document.addEventListener('input', (event) => {
        const input = event.target.closest('quantity-selector input[type="number"], form[action*="/cart/add"] input[name="quantity"]');
        if (!input) return;

        const selector = input.closest('quantity-selector');
        const effectiveMax = Math.min(Number(input.getAttribute('max')) || MAX_QUANTITY, MAX_QUANTITY);
        const requestedQuantity = Number(input.value);

        if (selector?.closest('cart-item')) {
            event.stopImmediatePropagation();
            if (!Number.isFinite(requestedQuantity) || requestedQuantity < 1) return;
            if (requestedQuantity > effectiveMax) {
                input.value = String(effectiveMax);
                showError(effectiveMax);
            }

            window.clearTimeout(inputTimers.get(selector));
            inputTimers.set(selector, window.setTimeout(() => {
                validateAndSetCartQuantity(selector, Math.min(requestedQuantity, effectiveMax));
            }, 300));
            return;
        }

        if (requestedQuantity <= effectiveMax) return;
        input.value = String(effectiveMax);
        showError(effectiveMax);
    }, true);

    ['keyup', 'change', 'blur'].forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
            if (event.target.closest('cart-item quantity-selector input[type="number"]')) {
                event.stopImmediatePropagation();
            }
        }, true);
    });

    const nativeAlert = window.alert.bind(window);
    window.alert = (message) => {
        if (String(message).includes('maximum quantity allowed for this product')) {
            const input = document.activeElement?.closest?.('quantity-selector')?.querySelector('input[type="number"]');
            showError(Math.min(Number(input?.getAttribute('max')) || MAX_QUANTITY, MAX_QUANTITY));
            return;
        }
        nativeAlert(message);
    };

    window.CartQuantityLimit = { max: MAX_QUANTITY, showError, checkInventory };

    window.customElements.whenDefined('product-form').then(() => {
        const ProductForm = window.customElements.get('product-form');
        if (!ProductForm || ProductForm.prototype.__quantityLimitPatched) return;

        const originalShowError = ProductForm.prototype.showError;
        ProductForm.prototype.showError = function showProductFormError(message) {
            originalShowError?.call(this, message);
            if (message) showError(null, message);
        };
        ProductForm.prototype.__quantityLimitPatched = true;
    });

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    applyLimit(node);
                    enforceUpsellLimits(node);
                    if (node.shadowRoot) observeUpsellRoot(node.shadowRoot);
                }
            });
        });
    });

    applyLimit();
    observeUpsellRoot(document);
    observer.observe(document.documentElement, { childList: true, subtree: true });
})();
